import { persistVaultTenantSnapshot } from './vault-tenant-snapshot.util';
import { replaceVaultTenantRoleInSession } from './vault-tenant-assignments.storage';
import { normalizeVaultRoleToken } from './vault-user-roles.util';
import {
  normalizeVaultTenantId,
  persistVaultTenantScopeToStorage,
} from './vault-tenant-scope.util';

export const VAULT_SESSION_PIN_KEY = 'vault_session_pin_v1';
export const VAULT_OPERATIVE_ROLE_PINNED_KEY = 'vault_operative_role_pinned';

export const VAULT_OPERATIVE_ROLE_KEY = 'vault_operative_role';
export const VAULT_DASHBOARD_ROLE_KEY = 'vault_dashboard_role';

export interface VaultSessionPin {
  role: string;
  tenant_id: string;
  tenant_name?: string;
}

export function readVaultSessionPin(): VaultSessionPin | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(VAULT_SESSION_PIN_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as VaultSessionPin;
      const role = (parsed?.role ?? '').trim();
      const tenant_id = (parsed?.tenant_id ?? '').trim();
      if (role && tenant_id) {
        return {
          role,
          tenant_id,
          ...(parsed.tenant_name?.trim() ? { tenant_name: parsed.tenant_name.trim() } : {}),
        };
      }
    }
  } catch {
    /* ignore */
  }

  const legacyRole = localStorage.getItem(VAULT_OPERATIVE_ROLE_PINNED_KEY)?.trim();
  if (!legacyRole) return null;
  const tenant_id =
    normalizeVaultTenantId(localStorage.getItem('tenant_id') ?? '') ||
    (localStorage.getItem('tenant_id') ?? '').trim();
  if (!tenant_id) {
    return { role: legacyRole, tenant_id: '' };
  }
  return { role: legacyRole, tenant_id };
}

export function writeVaultSessionPin(pin: VaultSessionPin): void {
  if (typeof localStorage === 'undefined') return;
  const role = pin.role.trim();
  const tenant_id = pin.tenant_id.trim();
  if (!role) return;
  const payload: VaultSessionPin = {
    role,
    tenant_id,
    ...(pin.tenant_name?.trim() ? { tenant_name: pin.tenant_name.trim() } : {}),
  };
  localStorage.setItem(VAULT_SESSION_PIN_KEY, JSON.stringify(payload));
  localStorage.setItem(VAULT_OPERATIVE_ROLE_PINNED_KEY, role);
}

export function clearVaultSessionPin(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(VAULT_SESSION_PIN_KEY);
  localStorage.removeItem(VAULT_OPERATIVE_ROLE_PINNED_KEY);
}

function syncRoleIntoUserData(role: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const raw = localStorage.getItem('user_data');
    if (!raw) return;
    const data = JSON.parse(raw) as Record<string, unknown>;
    data['role'] = role;
    localStorage.setItem('user_data', JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

/** Reaplica empresa + rol fijados localmente (Gestión de Usuarios). */
export function applyVaultSessionPinToStorage(): boolean {
  const pin = readVaultSessionPin();
  if (!pin?.role) return false;

  localStorage.setItem(VAULT_OPERATIVE_ROLE_KEY, pin.role);
  localStorage.setItem(VAULT_DASHBOARD_ROLE_KEY, pin.role);
  syncRoleIntoUserData(pin.role);

  const tenantId = normalizeVaultTenantId(pin.tenant_id) || pin.tenant_id.trim();
  if (!tenantId) return true;

  localStorage.setItem('tenant_id', tenantId);
  persistVaultTenantScopeToStorage(tenantId);
  replaceVaultTenantRoleInSession(tenantId, pin.role, pin.tenant_name);
  persistVaultTenantSnapshot({
    tenant_id: tenantId,
    tenant_name: pin.tenant_name,
    role: pin.role,
  });
  return true;
}

export function vaultSessionPinIsActive(): boolean {
  return !!readVaultSessionPin()?.role;
}

export function vaultSessionPinMatchesAuth(
  authRole: string,
  authTenantId: string,
): boolean {
  const pin = readVaultSessionPin();
  if (!pin?.role) return false;
  const roleOk = normalizeVaultRoleToken(authRole) === normalizeVaultRoleToken(pin.role);
  if (!pin.tenant_id.trim()) return roleOk;
  const pinTenant = normalizeVaultTenantId(pin.tenant_id) || pin.tenant_id.trim();
  const authTenant = normalizeVaultTenantId(authTenantId) || authTenantId.trim();
  return roleOk && !!authTenant && pinTenant === authTenant;
}
